import classnames from 'classnames'
import { Component, MouseEvent } from 'react'

import { Instrument } from '../../main/types'
import Sparkline from '../futures/components/CanvasSparkline'
import RowsStore from '../futures/stores/rowsStore'
import TilesStore from '../futures/stores/tilesStore'
import { cryptoDomains } from '../futures/utils'
import { generateColorScale } from '../futures/utils'
import { redGreenGradient } from '../shared/gradients'
import * as hoverChart from '../shared/hover-chart'
import CryptoActionCreators from './actions/cryptoActionCreators'
import BarChart from './barChart'
import LayoutNew from './components/redesign/Layout'
import MenuNew from './components/redesign/Menu'
import Menu from './menu'
import { CryptoTileTicker, PageType, TileMap } from './types'

var colorScale = generateColorScale(cryptoDomains, 'd')

interface LayoutProps {}

class Layout extends Component<LayoutProps> {
  render() {
    var layout = [
      ['BTCUSD', 'LTCUSD', 'ETHUSD', 'XRPUSD', 'BCHUSD'],
      ['BTCEUR', 'LTCEUR', 'ETHEUR', 'XRPEUR', 'BCHEUR'],
      ['EURUSD', 'LTCBTC', 'ETHBTC', 'XRPBTC', 'BCHBTC'],
    ]

    var tileWidth = 240
    var width = layout[0].length * tileWidth + 16

    return (
      <div style={{ margin: '0 auto', width }}>
        {layout.map((row, ri) => (
          <div key={ri}>
            {row.map((ticker) => (
              <Tile ticker={ticker} key={ticker} width={tileWidth} hoverOffsetX="0" hoverOffsetY="25" />
            ))}
            <div className="clearfix"></div>
          </div>
        ))}
      </div>
    )
  }
}

interface TileProps {
  ticker: string
  hoverOffsetX: string
  hoverOffsetY: string
  width: number
}

function getTileState(props: TileProps) {
  return TilesStore.getTile(props.ticker)
}

class Tile extends Component<TileProps> {
  state = getTileState(this.props)

  componentDidMount() {
    TilesStore.addChangeListener(this._onChange)
  }

  componentWillUnmount() {
    TilesStore.removeChangeListener(this._onChange)
  }

  _onChange = () => {
    this.setState(getTileState(this.props))
  }

  render() {
    var { ticker, label, last, prevClose, change, high, low, sparkline, sparklineDateChanges } = this.state
    var { hoverOffsetX, hoverOffsetY } = this.props

    var instrument = ticker === 'EURUSD' ? Instrument.Forex : Instrument.Crypto

    var format = (n: number) => (n >= 0 ? '+' : '') + n.toFixed(2) + '%'
    var places = last > 6 ? 2 : 4
    if (last < 0.001 && last > 0) {
      places = 7
    }
    var round = (n: number) => (+(Math.round(Number(n + 'e+' + places)) + 'e-' + places)).toFixed(places)

    var link =
      instrument === Instrument.Crypto ? '/crypto_charts.ashx?p=d1&t=' + ticker : '/forex_charts.ashx?p=d1&t=' + ticker

    const chartImage = hoverChart.getHoverChartImg(
      '@' + ticker.toLowerCase(),
      false,
      instrument === Instrument.Crypto
        ? FinvizSettings.hoverCharts?.crypto?.timeframe
        : FinvizSettings.hoverCharts?.forex?.timeframe,
      instrument === Instrument.Crypto
        ? FinvizSettings.hoverCharts?.crypto?.dateRange
        : FinvizSettings.hoverCharts?.forex?.dateRange
    )

    const boxover = `cssbody=[hoverchart] cssheader=[tabchrthdr] body=[${chartImage.img}] offsetx=[${hoverOffsetX}] offsety=[${hoverOffsetY}] delay=[100]`

    var color = colorScale(change)

    var range = (high - low) * 0.1
    var highlightHigh = last > high - range
    var highlightLow = last < low + range
    var highlightStyle = {
      background: '#fbdb19',
      color: '#3e424f',
    }

    var opacityChange = Math.min(Math.abs(change), 1)
    var textOpacity = 0.5 + (0.2 / 1) * opacityChange
    var highLowStyle = { color: `rgba(255, 255, 255, ${textOpacity})` }

    var changeAbsolute = round(last - prevClose)
    if (ticker === 'XRPBTC') {
      changeAbsolute = (last - prevClose).toFixed(places)
    }

    return (
      <a
        className={'tile is-' + ticker}
        href={link}
        style={{ width: this.props.width, marginLeft: 1 }}
        data-boxover={boxover}
      >
        <div className="tile_content" style={{ backgroundColor: color }}>
          <span className="tile_label">{label}</span>
          <span className="tile_last-price">{round(last)}</span>
          <span className="tile_change">
            {format(change)}
            <span className="tile_dot"> &#8226; </span>
            {changeAbsolute.replace('-', '')}
          </span>
          <div className="tile_high-low" style={highLowStyle}>
            <div style={highlightHigh ? highlightStyle : {}}>
              <span className="tile_high-label">H</span> {round(high)}
            </div>
            <div style={highlightLow ? highlightStyle : {}}>
              <span className="tile_low-label">L</span> {round(low)}
            </div>
          </div>
          <Sparkline
            width={this.props.width}
            height={72}
            data={sparkline}
            dateChanges={sparklineDateChanges}
            change={change}
            changeDomain={1}
          />
        </div>
      </a>
    )
  }
}

function getCryptoQuotesState() {
  return {
    tiles: TilesStore.getTiles(),
  }
}

class CryptoQuotes extends Component {
  state = getCryptoQuotesState()

  componentDidMount() {
    TilesStore.addChangeListener(this._onChange)
  }

  componentWillUnmount() {
    TilesStore.removeChangeListener(this._onChange)
  }

  _onChange = () => {
    this.setState(getCryptoQuotesState())
  }

  render() {
    var timeframes = {
      link: () => '#',
      onClick: (timeframe: string, e: MouseEvent) => {
        e.preventDefault()
        CryptoActionCreators.changeTimeframe(timeframe)
      },
      active: RowsStore.getTimeframe(),
      timeframes: [
        { key: 'i5', value: '5M' },
        { key: 'h', value: 'H' },
        { key: 'd', value: 'D' },
        { key: 'w', value: 'W' },
        { key: 'm', value: 'M' },
      ],
    }

    // TODO bud spravit special API alebo preratavat
    var tiles = this.state.tiles as TileMap

    var relativePerf = { USD: 0 }
    Object.keys(tiles).forEach((ticker) => {
      var tile = tiles[ticker as CryptoTileTicker]
      if (ticker.indexOf('USD') === -1 || ticker === 'EURUSD') {
        return
      }
      var inverse = ticker.substring(0, 3) === 'USD'
      // @ts-ignore
      relativePerf[ticker.replace('USD', '')] =
        (inverse ? tile.prevClose / tile.last - 1 : tile.last / tile.prevClose - 1) * 100 // todo round
    })

    var rows = Object.keys(relativePerf)
      .map((key) => ({
        label: key,
        // @ts-ignore
        value: relativePerf[key],
      }))
      .sort((a, b) => b.value - a.value)

    const isRedesignEnabled = FinvizSettings.hasRedesignEnabled
    const MenuComponent = isRedesignEnabled ? MenuNew : Menu
    const LayoutComponent = isRedesignEnabled ? LayoutNew : Layout
    //
    return (
      <div className="futures forex flex flex-col">
        <MenuComponent activePage={PageType.Quotes} timeframes={timeframes} />
        <LayoutComponent />
        {!isRedesignEnabled && (
          <>
            <div className="clearfix" />
            <br />
          </>
        )}
        <div
          className={classnames('perf-chart-container', {
            'my-7': isRedesignEnabled,
          })}
        >
          <div className="bar-chart-wrapper">
            <BarChart width={740} data={rows} period="d" {...(isRedesignEnabled && { gradient: redGreenGradient })} />
          </div>
        </div>
      </div>
    )
  }
}

export default CryptoQuotes
